import { Classes, H6 } from "@blueprintjs/core";
import { Form, Input } from "decorator-form";
import React from "react";
import { Entity, Field } from "typegraph";

import convertToHtml from "common/fragments/ConvertToHtml";
import { ID } from "common/entities/IEntity";
import InputType from "common/form/InputType";
import { IPageableListQuery } from "common/models/IPageableListQuery";
import PageLinkView from "common/views/pageLink/PageLink.view";
import { EntityTable } from "core/components/EntityTable";
import IEntity from "core/util/IEntity";
import CountResult from "panels/organizationParticipant/entities/CountResult";
import constants from "../constants";
import entityMeta from "../entityMeta";
import HtmlDefault from "common/fragments/HtmlDefault";
import { GlossaryTable } from "./GlossaryTable";

@Entity(entityMeta.GLOSSARY_TERM)
@Form()
class GlossaryTerm implements IEntity {
	@Field()
	public id: ID;

	@Field()
	@Input({
		type: InputType.TEXT,
		title: "Term",
		required: true
	})
	public term: string;

	@Field()
	@Input({
		type: InputType.TEXT,
		title: "Alternatives"
	})
	public alternatives: string;

	@Field()
	@Input({
		type: InputType.TEXT_HTML,
		title: "Definition",
		required: true
	})
	public definition: string;
}

function mapQueryParams(args: any) {
	const params = {};

	Object.entries(args).forEach(([key, value]) => {
		if (value) {
			params[key] = value;
		}
	});

	return { params };
}

class GlossaryTermQuery implements IPageableListQuery<GlossaryTerm> {
	@Field({
		entity: GlossaryTerm,
		aliasFor: entityMeta.GLOSSARY_TERM.many,
		params: mapQueryParams
	})
	public entities: null | GlossaryTerm[];

	@Field({
		entity: CountResult,
		aliasFor: "glossaryTerm_count",
		params: mapQueryParams
	})
	public count: CountResult;
}

interface IGlossaryTermListProps {
	knowledgeBaseId: ID;
	canCreate?: boolean;
	canDelete?: boolean;
	canUpdate?: boolean;
	showPaginationOptions?: boolean;
}

export default function GlossaryTermList(props: IGlossaryTermListProps) {
	const params = {};
	const { canCreate, canDelete, canUpdate, showPaginationOptions } = props;

	if (props.knowledgeBaseId) {
		params["knowledgeBaseId"] = props.knowledgeBaseId;
	} else {
		return null;
	}

	return (
		<React.Fragment>
			<GlossaryTable
				pagingQuery={GlossaryTermQuery}
				formClass={GlossaryTerm}
				params={params}
				canCreate={canCreate}
				canDelete={canDelete}
				canUpdate={canUpdate}
				showPaginationOptions={showPaginationOptions}
				columns={[
					{
						key: "term",
						label: "Term",
						sort: "term",
						renderCell: ({ data }) => {
							return (
								<PageLinkView
									viewKey="glossaryTerm.link"
									preserveParams={false}
									params={{
										[constants.PARAM_TERM_ID]: data.id
									}}
								>
									<HtmlDefault html={data.term} />
									{data.alternatives && (
										<p className={Classes.TEXT_MUTED}>
											{`Alternatives: ${
												data.alternatives
											}`}
										</p>
									)}
								</PageLinkView>
							);
						}
					},
					{
						key: "definition",
						label: "Definition",
						sort: "term",
						renderCell: ({ data }) => {
							return (
								<PageLinkView
									viewKey="glossaryDefinition.link"
									preserveParams={false}
									params={{
										[constants.PARAM_TERM_ID]: data.id
									}}
								>
									<HtmlDefault html={data.definition} />
								</PageLinkView>
							);
						}
					}
				]}
				onBeforeSave={term => {
					const actionParams = {};

					if (term.id) {
						actionParams["id"] = term.id;
					} else {
						actionParams["knowledgeBaseId"] = props.knowledgeBaseId;
					}

					actionParams["term"] = term.term;

					if (term.alternatives) {
						actionParams["alternatives"] = term.alternatives;
					}

					actionParams["definition"] = convertToHtml(term.definition);

					return actionParams;
				}}
			/>
		</React.Fragment>
	);
}



// WEBPACK FOOTER //
// ./src/panels/kb/glossary/GlossaryTermList.tsx